/**
 * DashboardStats — Compact HUD
 *
 * Replaces the previous 294-line version that had two full Recharts graphs.
 * Design: actionable clarity over information density.
 *
 * Layout:
 *   Desktop  → 4-column KPI strip (horizontal), each card has a 28-day sparkline
 *   Mobile   → 2×2 KPI grid, sparklines hidden, "New Quote" CTA prominent
 *
 * No external charting library — sparklines are inline SVG paths computed
 * from real data. Zero Recharts import, ~100kb bundle savings.
 */

import { useMemo } from 'react';
import { cn, formatCurrency } from '@/lib/utils';
import {
  FileText, Zap, IndianRupee, AlertCircle,
  ArrowUpRight, ArrowDownRight, ArrowRight, Minus,
} from 'lucide-react';
import type { Quotation } from '@/types/quotation';
import type { Invoice } from '@/types/invoice';

interface DashboardStatsProps {
  quotations: Quotation[];
  invoices:   Invoice[];
}

// ── Pure sparkline from an array of numbers ───────────────────────────────────
function Sparkline({ data, color = '#10b981', className }: { data: number[]; color?: string; className?: string }) {
  const W = 80, H = 28;

  if (!data || data.length < 2) {
    return <svg width={W} height={H} className={className} />;
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * W;
    const y = H - ((v - min) / range) * (H - 4) - 2;
    return `${x},${y}`;
  });

  const pathD = `M ${pts.join(' L ')}`;
  const areaD = `M ${pts[0]} L ${pts.join(' L ')} L ${W},${H} L 0,${H} Z`;

  return (
    <svg width={W} height={H} className={cn('overflow-visible', className)} aria-hidden>
      <defs>
        <linearGradient id={`sg-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity={0.25} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#sg-${color.replace('#', '')})`} />
      <path d={pathD} stroke={color} strokeWidth={1.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Build 28-day daily series from quotation / invoice data ───────────────────
function buildDailySeries(items: Quotation[] | Invoice[], dateKey: (item: Quotation | Invoice) => string, valueKey?: (item: Quotation | Invoice) => number): number[] {
  const today = new Date();
  return Array.from({ length: 28 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (27 - i));
    const dateStr = d.toISOString().split('T')[0];
    const dayItems = items.filter(item => dateKey(item).startsWith(dateStr));
    return valueKey ? dayItems.reduce((s, item) => s + valueKey(item), 0) : dayItems.length;
  });
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
interface KPICardProps {
  label:      string;
  value:      string | number;
  sub:        string;
  trend:      'up' | 'down' | 'neutral';
  trendLabel: string;
  sparkData:  number[];
  sparkColor: string;
  icon:       React.ElementType;
  accent:     string;
}

import { motion } from 'framer-motion';

function KPICard({ label, value, sub, trend, trendLabel, sparkData, sparkColor, icon: Icon, accent }: KPICardProps) {
  const TrendIcon = trend === 'up'
    ? <ArrowUpRight   className="h-2.5 w-2.5 text-emerald-400" />
    : trend === 'down'
    ? <ArrowDownRight className="h-2.5 w-2.5 text-rose-400" />
    : <Minus          className="h-2.5 w-2.5 text-muted-foreground" />;

  const trendColor = trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-rose-400' : 'text-muted-foreground';

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      className={cn(
        'group relative rounded-2xl bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-white/5 p-3 sm:p-4 shadow-black/5 shadow-sm',
        'flex flex-col justify-between gap-2 sm:gap-3 min-h-[90px] sm:min-h-[100px] overflow-hidden',
        'hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-all duration-200 ease-out active:scale-[0.985]',
      )}
    >

      {/* Top row: icon + sparkline */}
      <div className="flex items-start justify-between relative z-10">
        <div className={cn('h-6 sm:h-7 w-6 sm:w-7 rounded-lg flex items-center justify-center bg-gradient-to-br shrink-0', accent)}>
          <Icon className="h-3 sm:h-3.5 w-3 sm:w-3.5 text-black" strokeWidth={3} />
        </div>
        {/* Sparkline — hidden on mobile for density */}
        <div className="relative">
          <Sparkline
            data={sparkData}
            color={sparkColor}
            className="hidden sm:block opacity-40 group-hover:opacity-100 transition-opacity scale-75 sm:scale-90 origin-top-right duration-700"
          />
          <div className="absolute top-0 right-0 h-full w-3 sm:w-4 bg-gradient-to-l from-white dark:from-zinc-950 to-transparent sm:hidden" />
        </div>
      </div>

      {/* Value */}
      <div className="mt-0.5 sm:mt-1 relative z-10 min-w-0">
        <p className="text-[9px] sm:text-[10px] font-medium text-zinc-500 uppercase tracking-[0.08em] mb-0.5 sm:mb-1 truncate">{label}</p>
        <div className="text-base sm:text-xl md:text-2xl font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight tabular-nums leading-none truncate break-words">
          {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
        </div>
        <div className="flex items-center justify-between mt-1 sm:mt-1.5 gap-2 min-w-0">
           <p className="text-[10px] text-zinc-500 dark:text-zinc-400 truncate flex-1 leading-relaxed">{sub}</p>
           <div className="flex items-center gap-1 shrink-0">
             <div className="h-1 w-1 rounded-full bg-emerald-500/50" />
           </div>
        </div>
      </div>

      {/* Footer Trend */}
      <div className="pt-2 sm:pt-3 mt-1 border-t border-zinc-100 dark:border-white/5 flex items-center justify-between relative z-10 gap-2">
        <div className="flex items-center gap-1 min-w-0">
          {TrendIcon}
          <span className={cn('text-[10px] font-medium tracking-wide truncate', trendColor)}>{trendLabel}</span>
        </div>
        <ArrowRight className="h-3 w-3 text-zinc-400 group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors shrink-0" />
      </div>
    </motion.div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export function DashboardStats({ quotations, invoices }: DashboardStatsProps) {
  const safeQuotes   = useMemo(() => quotations || [], [quotations]);
  const safeInvoices = useMemo(() => invoices   || [], [invoices]);

  const totalQuotes     = safeQuotes.length;
  const draftQuotes     = safeQuotes.filter(q => q?.metadata?.status === 'draft').length;
  const finalizedQuotes = safeQuotes.filter(q => q?.metadata?.status === 'finalized').length;
  const pendingQuotes   = safeQuotes.filter(q => q?.metadata?.status === 'pending_approval').length;
  const conversionRate  = totalQuotes > 0 ? Math.round((finalizedQuotes / totalQuotes) * 100) : 0;

  const nonDraftInvoices    = safeInvoices.filter(i => i.status !== 'draft' && i.status !== 'cancelled');
  const totalInvoicedAmount = nonDraftInvoices.reduce((s, i) => s + (i.financials?.grandTotal ?? 0), 0);
  const totalPaidAmount     = nonDraftInvoices.reduce((s, i) => {
    const paid = i.status === 'paid' ? (i.financials?.grandTotal ?? 0) : (i.financials?.paidAmount ?? 0);
    return s + paid;
  }, 0);
  const totalPending        = Math.max(0, totalInvoicedAmount - totalPaidAmount);

  // 28-day sparklines derived from real data
  const quotesSpark = useMemo(() =>
    buildDailySeries(safeQuotes, q => (q as Quotation).system?.createdAt ?? ''),
    [safeQuotes],
  );
  const invoiceSpark = useMemo(() =>
    buildDailySeries(
      safeInvoices,
      i => (i as Invoice).createdAt ?? '',
      i => (i as Invoice).financials?.grandTotal ?? 0,
    ),
    [safeInvoices],
  );
  const pendingSpark = useMemo(() =>
    buildDailySeries(
      safeInvoices.filter(i => {
        if (i.status === 'paid') return false;
        return (i.financials?.paidAmount ?? 0) < (i.financials?.grandTotal ?? 0);
      }),
      i => (i as Invoice).createdAt ?? '',
    ),
    [safeInvoices],
  );
  const conversionSpark = useMemo(() => {
    // Weekly conversion rate for last 28 days (sampled at 7-day granularity × 4 weeks)
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const cutoff = new Date(today);
      cutoff.setDate(today.getDate() - (6 - i) * 4);
      const window = safeQuotes.filter(q => {
        const d = new Date(q.system?.createdAt ?? '');
        return !isNaN(d.getTime()) && d <= cutoff;
      });
      const fin = window.filter(q => q?.metadata?.status === 'finalized').length;
      return window.length > 0 ? Math.round((fin / window.length) * 100) : 0;
    });
  }, [safeQuotes]);

  const kpis: KPICardProps[] = [
    {
      label:      'Active Quotes',
      value:      totalQuotes,
      sub:        `${draftQuotes} drafts · ${pendingQuotes} pending`,
      trend:      totalQuotes > 0 ? 'up' : 'neutral',
      trendLabel: `${finalizedQuotes} finalized`,
      sparkData:  quotesSpark,
      sparkColor: '#10b981',
      icon:       FileText,
      accent:     'from-emerald-600 to-emerald-400',
    },
    {
      label:      'Conversion Rate',
      value:      `${conversionRate}%`,
      sub:        `${finalizedQuotes} of ${totalQuotes} quotes`,
      trend:      conversionRate >= 50 ? 'up' : conversionRate > 0 ? 'neutral' : 'down',
      trendLabel: conversionRate >= 50 ? 'On target' : 'Below 50%',
      sparkData:  conversionSpark,
      sparkColor: '#f59e0b',
      icon:       Zap,
      accent:     'from-amber-500 to-amber-300',
    },
    {
      label:      'Invoiced Revenue',
      value:      formatCurrency(totalInvoicedAmount),
      sub:        `${nonDraftInvoices.length} invoices raised`,
      trend:      totalInvoicedAmount > 0 ? 'up' : 'neutral',
      trendLabel: `${formatCurrency(totalPaidAmount)} received`,
      sparkData:  invoiceSpark,
      sparkColor: '#06b6d4',
      icon:       IndianRupee,
      accent:     'from-cyan-500 to-teal-400',
    },
    {
      label:      'Pending Revenue',
      value:      formatCurrency(totalPending),
      sub:        totalPending > 0 ? 'Follow-up required' : 'All clear',
      trend:      totalPending > 0 ? 'down' : 'up',
      trendLabel: totalPending > 0 ? 'Outstanding' : 'Fully paid',
      sparkData:  pendingSpark,
      sparkColor: '#f43f5e',
      icon:       AlertCircle,
      accent:     'from-rose-500 to-pink-400',
    },
  ];

  return (
    // Mobile-first: 1 col on mobile, 2 cols on sm, 4 cols on lg
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 w-full">
      {kpis.map(kpi => (
        <KPICard key={kpi.label} {...kpi} />
      ))}
    </div>
  );
}
